import { BellSimpleIcon, CaretDownIcon } from '@phosphor-icons/react';
import { Dropdown } from 'antd';
import { NavLink, useNavigate } from 'react-router-dom';

import logo from '@/assets/images/field-pro-logo.webp';
import { SearchBar } from '@/components/common/search-bar';
import { UserAvatar } from '@/components/common/user-avatar';
import { nav } from '@/config/nav';
import { routes } from '@/config/routes';
import { useLogout } from '@/features/auth';
import { useAuth } from '@/providers/auth-provider';

import styles from './top-nav.module.scss';

/**
 * Dark-green top app-bar shell chrome for FieldPro Web. Left: FIELDPRO logo.
 * Center: flat primary nav (Devices | Map) from `config/nav`. Right: a search
 * slot, a notification bell, and a profile avatar dropdown (Edit profile /
 * Sign out). Layout chrome — legitimately consumes the auth feature.
 */
export function TopNav() {
  const navigate = useNavigate();
  const { user, clearSession } = useAuth();
  const logout = useLogout();

  const signOut = async () => {
    await logout.mutateAsync().catch(() => undefined);
    clearSession();
    navigate(routes.login);
  };

  const displayName = user ? `${user.firstName} ${user.lastName}` : '';

  return (
    <header className={styles.bar}>
      <button
        type="button"
        className={styles.logoBtn}
        aria-label="Go to home"
        onClick={() => navigate(routes.home)}
      >
        <img src={logo} alt="FieldPro" className={styles.logo} />
      </button>

      <nav className={styles.nav} aria-label="Primary">
        {nav.map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            className={({ isActive }) =>
              isActive ? `${styles.link} ${styles.linkActive}` : styles.link
            }
          >
            <span className={styles.linkIcon}>{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className={styles.actions}>
        <div className={styles.search}>
          <SearchBar onSearch={() => undefined} placeholder="Search" aria-label="Search" />
        </div>

        <button
          type="button"
          className={styles.iconBtn}
          aria-label="Notifications"
          onClick={() => navigate(routes.notifications)}
        >
          <BellSimpleIcon size={20} />
        </button>

        {user ? (
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                {
                  key: 'profile',
                  label: 'Edit profile',
                  onClick: () => navigate(routes.profile),
                },
                {
                  key: 'logout',
                  label: 'Sign out',
                  onClick: signOut,
                },
              ],
            }}
          >
            <button type="button" className={styles.profileBtn} aria-label="Account menu">
              <UserAvatar name={displayName} size="md" />
              <CaretDownIcon size={14} className={styles.caret} />
            </button>
          </Dropdown>
        ) : null}
      </div>
    </header>
  );
}
